<template>
  <div class="q-mx-auto q-py-sm" style="max-width: 900px">
    <q-card flat bordered>
      <div class="row q-pa-md">
        <div class="col">
          <div
            class="text-body1 text-weight-bold"
            :class="$q.dark.mode ? '' : 'text-primary '"
          >
            Broker Analytics of {{ symbol }}
            <div class="text-body2 q-pl-xs" v-if="tabPeriod !== 'daily'">
              asof: {{ store.state.market.asOf }}
            </div>
            <div class="text-body2 q-pl-xs" v-if="tabPeriod === 'daily'">
              asof: {{ selectedDate.replaceAll("/", "-") }}
            </div>
          </div>
        </div>
        <div class="col">
          <q-input
            v-if="tabPeriod === 'daily'"
            dense
            filled
            v-model="selectedDate"
            capslock
            mask="date"
            :rules="['date']"
            class="text-bold"
          >
            <q-popup-proxy
              ref="qDateProxy"
              transition-show="scale"
              transition-hide="scale"
            >
              <q-date
                v-model="selectedDate"
                @input="() => $refs.qDateProxy.hide()"
                @update:model-value="loadDataDaily(selectedDate, symbol)"
              />
            </q-popup-proxy>
            <q-icon
              name="event"
              class="text-grey text-h6 date-icon cursor-pointer q-pt-sm"
            />
          </q-input>
        </div>
      </div>

      <q-tabs
        v-model="tabPeriod"
        dense
        outside-arrows
        mobile-arrows
        :class="$q.dark.mode ? 'bg-grey-10' : 'text-grey-9 bg-grey-4'"
        active-color="white"
        :active-bg-color="$q.dark.mode ? 'grey-9' : 'blue-4'"
        :indicator-color="$q.dark.mode ? 'grey-7' : 'blue-5'"
        align="justify"
        narrow-indicator
      >
        <q-tab name="daily" label="Daily" no-caps />
        <q-separator vertical size="md" />
        <q-tab name="weekly" label="Weekly" no-caps />
        <q-separator vertical size="md" />
        <q-tab name="monthly" label="Monthly" no-caps />
      </q-tabs>

      <q-separator color="white" size="sm" />

      <q-tabs
        v-model="tabAction"
        dense
        outside-arrows
        mobile-arrows
        :class="$q.dark.mode ? 'bg-grey-10' : 'text-grey-9 bg-grey-4'"
        active-color="white"
        :active-bg-color="$q.dark.mode ? 'grey-9' : 'blue-4'"
        :indicator-color="$q.dark.mode ? 'grey-7' : 'blue-5'"
        align="justify"
        narrow-indicator
      >
        <q-tab name="top-bought" label="Top Bought" no-caps />
        <q-separator vertical size="md" />
        <q-tab name="top-sold" label="Top Sold" no-caps />
        <q-separator vertical size="md" />
        <q-tab name="top-net-holding" label="Top Holding" no-caps />
        <q-separator vertical size="md" />
        <q-tab name="top-net-released" label="Top Released" no-caps />
      </q-tabs>

      <q-separator />

      <q-tab-panels v-model="tabAction" animated>
        <q-tab-panel class="q-pa-none" :name="tabAction">
          <q-table
            flat
            bordered
            square
            :class="
              $q.dark.mode
                ? 'table-sticky-header-column-dark'
                : 'table-sticky-header-column'
            "
            :rows="seriesData"
            :columns="
              tabAction == 'top-bought' || tabAction == 'top-net-holding'
                ? columnsBuy
                : columnsSell
            "
            row-key="name"
            :filter-method="filterBrokerAnalytics"
            :filter="filterBroker"
            separator="cell"
            binary-state-sort
            :pagination="pagination"
          >
            <template v-slot:top-left>
              <div class="row">
                <q-input
                  class="q-mr-md live-search"
                  bordered
                  dense
                  debounce="300"
                  v-model="filterBroker"
                  placeholder="Filter Broker"
                >
                  <template v-slot:append>
                    <q-icon name="search" />
                  </template>
                </q-input>
              </div>
            </template>
            <template v-slot:top-right>
              <div class="row">
                <q-btn
                  @click="filterBroker = ''"
                  color="dark"
                  label="Clear Filter"
                  flat
                  dense
                >
                  <q-icon color="red" name="clear" class="q-ml-md" />
                </q-btn>
              </div>
            </template>
            <template v-slot:body-cell-buyer="props">
              <q-td :props="props">
                <q-item
                  class="row justify-center"
                  dense
                  clickable
                  :to="{
                    path: '/brokers-analytics-pro',
                    query: {
                      brokerID: props.value,
                      transaction: 'Purchase',
                    },
                  }"
                >
                  <div class="col">
                    {{ props.value }}
                    <q-tooltip>{{
                      brokerLists.find(
                        (broker) => broker.broker_no === props.value
                      )?.broker_name
                    }}</q-tooltip>
                  </div>
                </q-item>
              </q-td>
            </template>

            <template v-slot:body-cell-seller="props">
              <q-td :props="props">
                <q-item
                  class="row justify-center"
                  dense
                  clickable
                  :to="{
                    path: '/brokers-analytics-pro',
                    query: {
                      brokerID: props.value,
                      transaction: 'Selling',
                    },
                  }"
                >
                  <div class="col">
                    {{ props.value }}
                    <q-tooltip>{{
                      brokerLists.find(
                        (broker) => broker.broker_no === props.value
                      )?.broker_name
                    }}</q-tooltip>
                  </div>
                </q-item>
              </q-td>
            </template>
            <template v-slot:body-cell-quantity_percentage="props">
              <q-td :props="props">
                <q-linear-progress
                  size="24px"
                  stripe
                  rounded
                  :value="props.value / 100"
                  :color="
                    ['top-bought', 'top-net-holding'].includes(tabAction)
                      ? 'secondary'
                      : 'red'
                  "
                >
                  <div class="absolute-full flex flex-center">
                    <q-badge
                      color="white"
                      :text-color="
                        ['top-bought', 'top-net-holding'].includes(tabAction)
                          ? 'secondary'
                          : 'red'
                      "
                      :label="`${props.row.quantity_percentage?.toFixed(2)} %`"
                    />
                  </div>
                </q-linear-progress>
              </q-td>
            </template>
            <template v-slot:top-row>
              <q-tr
                class="text-bold"
                :class="$q.dark.mode ? 'bg-grey-8' : 'bg-blue-2'"
              >
                <q-td :class="$q.dark.mode ? 'bg-grey-8' : 'bg-blue-2'"
                  >Broker Count : {{ seriesData.length }}</q-td
                >
                <q-td class="text-center">Total</q-td>
                <q-td class="text-center">
                  {{ sumBy(seriesData, "quantity") }}
                </q-td>
                <q-td class="text-center">
                  {{ sumBy(seriesData, "amount").toFixed(2) }}
                </q-td>
                <q-td class="text-center">
                  {{
                    (
                      sumBy(seriesData, "amount") /
                      sumBy(seriesData, "quantity")
                    ).toFixed(2)
                  }}
                </q-td>
              </q-tr>
            </template>
          </q-table>

          <q-card square flat bordered class="q-mt-sm">
            <p class="text-subtitle2 q-mx-sm">
              Brokerwise
              {{
                tabAction.split("-").slice(-1)[0].charAt(0).toUpperCase() +
                tabAction.split("-").slice(-1)[0].slice(1)
              }}

              Quantity of
              {{ symbol }}
            </p>
            <div class="q-pa-sm">
              <VueApexCharts
                :options="chartComputed.options"
                :series="chartComputed.series"
                type="bar"
                height="350"
              />
            </div>
          </q-card>
        </q-tab-panel>
      </q-tab-panels>
    </q-card>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useQuasar } from "quasar";
import useData from "src/use/data";
import { sumBy } from "lodash";

import VueApexCharts from "vue3-apexcharts";

const props = defineProps({
  symbol: {
    type: String,
    required: true,
  },
});

const $q = useQuasar();
const {
  brokerList,
  netHoldingDateWise,
  netReleasedDateWise,
  buyerStockDateWise,
  sellerStockDateWise,

  netHoldingDayWise,
  netReleasedDayWise,
  buyerStockDayWise,
  sellerStockDayWise,
} = useData();

const store = useStore();

const selectedDate = ref("");
const brokerLists = ref([]);

const topBuy = ref([]);
const topSell = ref([]);
const netHold = ref([]);
const netReleased = ref([]);

const tabPeriod = ref("daily");
const tabAction = ref("top-bought");

const filterBroker = ref("");

const filterBrokerAnalytics = (rows) => {
  return rows.filter((row) => {
    return (
      filterBroker.value.broker === "" ||
      row.seller?.toLowerCase() === filterBroker.value ||
      row.buyer?.toLowerCase() === filterBroker.value
    );
  });
};

function calculateQuantityTotal(data) {
  const totalQuantity = data.reduce((total, item) => total + item.quantity, 0);
  return data.map((item) => ({
    ...item,
    quantity_total: totalQuantity,
    quantity_percentage: (item.quantity / totalQuantity) * 100,
  }));
}

async function loadDataDaily(date, symbol) {
  $q.loading.show({});
  try {
    const formattedDate = date.replaceAll("/", "-");
    const [buyRes, sellRes, holdRes, releaseRes] = await Promise.all([
      buyerStockDateWise(formattedDate, formattedDate, symbol),
      sellerStockDateWise(formattedDate, formattedDate, symbol),
      netHoldingDateWise(formattedDate, formattedDate, symbol),
      netReleasedDateWise(formattedDate, formattedDate, symbol),
    ]);

    topBuy.value = calculateQuantityTotal(buyRes.data);
    topSell.value = calculateQuantityTotal(sellRes.data);
    netHold.value = calculateQuantityTotal(holdRes.data);
    netReleased.value = calculateQuantityTotal(releaseRes.data);
  } catch (error) {
    console.error("Error loading data:", error);
  } finally {
    $q.loading.hide();
  }
}

async function loadData(argTabPeriod = 5, symbol) {
  try {
    $q.loading.show({});
    const buyRes = await buyerStockDayWise(argTabPeriod, symbol);
    const sellRes = await sellerStockDayWise(argTabPeriod, symbol);
    const holdRes = await netHoldingDayWise(argTabPeriod, symbol);
    const releaseRes = await netReleasedDayWise(argTabPeriod, symbol);

    topBuy.value = calculateQuantityTotal(buyRes.data);
    topSell.value = calculateQuantityTotal(sellRes.data);
    netHold.value = calculateQuantityTotal(holdRes.data);
    netReleased.value = calculateQuantityTotal(releaseRes.data);

    selectedSymbol.value = seriesData()[0]?.symbol || null;
  } catch (error) {
    console.error("Error loading data:", error);
  } finally {
    $q.loading.hide();
  }
}

const columnsBuy = [
  {
    name: "buyer",
    label: "Buyer",
    field: "buyer",
    sortable: true,
    align: "left",
  },
  {
    name: "quantity_percentage",
    label: "Qnty %",
    field: "quantity_percentage",
    sortable: true,
    sort: (a, b, rowA, rowB) => parseInt(a, 10) - parseInt(b, 10),
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
  {
    name: "quantity",
    label: "Quantity",
    field: "quantity",
    sortable: true,
    format: (val, row) => {
      // const percentage = (row.quantity / row.quantity_total) * 100;
      // return `${val} (${percentage?.toFixed(2)})`;
      return val;
    },
    align: "center",
  },

  {
    name: "amount",
    label: "Amount",
    field: "amount",
    sortable: true,
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
  {
    name: "rate",
    label: "Rate",
    field: "rate",
    sortable: true,
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
];

const columnsSell = [
  {
    name: "seller",
    label: "Seller",
    field: "seller",
    sortable: true,
    align: "left",
  },
  {
    name: "quantity_percentage",
    label: "Qnty %",
    field: "quantity_percentage",
    sortable: true,
    sort: (a, b, rowA, rowB) => parseInt(a, 10) - parseInt(b, 10),
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
  {
    name: "quantity",
    label: "Quantity",
    field: "quantity",
    sortable: true,
    format: (val, row) => {
      // const percentage = (row.quantity / row.quantity_total) * 100;
      // return `${val} (${percentage?.toFixed(2)})`;
      return val;
    },
    align: "center",
  },

  {
    name: "amount",
    label: "Amount",
    field: "amount",
    sortable: true,
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
  {
    name: "rate",
    label: "Rate",
    field: "rate",
    sortable: true,
    format: (val, row) => {
      return parseFloat(val).toFixed(2);
    },
    align: "center",
  },
];

const pagination = ref({
  sortBy: "quantity",
  descending: true,
  rowsPerPage: 10,
});

const seriesData = computed(() => {
  const dataMap = {
    "top-bought": topBuy.value,
    "top-sold": topSell.value,
    "top-net-holding": netHold.value,
    "top-net-released": netReleased.value,
  };

  return dataMap[tabAction.value] || [];
});

const chartDataSlice = computed(() => {
  return seriesData.value
    .slice()
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 10);
});

const chartComputed = computed(() => {
  const condition =
    tabAction.value === "top-bought" || tabAction.value === "top-net-holding";

  const categories = condition
    ? chartDataSlice.value.map((item) => item.buyer)
    : chartDataSlice.value.map((item) => item.seller);

  const text = condition ? "Buyer" : "Seller";

  const series = [
    {
      data: chartDataSlice.value.map((item) => item.quantity),
    },
  ];

  const options = {
    chart: {
      type: "bar",
      height: 350,
      events: {
        dataPointSelection: false,
        markerClick: false,
        legendClick: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "75%",
        endingShape: "rounded",
        dataLabels: {
          position: "top",
        },
      },
    },
    tooltip: $q.dark.mode ? { theme: "dark" } : { theme: "light" },

    legend: {
      show: true,
      position: "top",
      labels: {
        colors: $q.dark.mode ? "#fff" : "#000",
      },
    },
    xaxis: {
      categories: categories,
      title: {
        text: text,
      },
      labels: {
        style: {
          colors: $q.dark.mode ? "#fff" : "#000",
        },
      },
    },
    yaxis: {
      title: {
        text: "Quantity",
      },
      labels: {
        formatter: function (val) {
          return val?.toFixed(1);
        },
        offsetY: -40,
        style: {
          colors: $q.dark.mode ? "#fff" : "#000",
        },
      },
    },
  };

  return { series, options };
});

watch(props, (newVal, oldVal) => {
  loadDataDaily(selectedDate.value, newVal.symbol);
});

watch(tabPeriod, (newVal) => {
  const tabPeriodMap = {
    weekly: 5,
    monthly: 21,
  };
  if (newVal === "daily") {
    loadDataDaily(selectedDate.value, props.symbol);
  } else {
    loadData(tabPeriodMap[newVal], props.symbol);
  }
});

onMounted(async () => {
  if (store.state.market.asOf) {
    selectedDate.value = store.state.market.asOf;
  } else {
    await store.dispatch("market/marketStatusNew");
    selectedDate.value = store.state.market.asOf;
  }

  brokerLists.value = (await brokerList()).data;
  loadDataDaily(selectedDate.value, props.symbol);
});
</script>
